import React from 'react';
import { motion } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow';
import { Checkbox } from '@/components/ui/checkbox';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { StatusBadge } from './StatusBadge';
import { PriorityBadge } from './PriorityBadge';
import { PlatformChip } from './PlatformChip';
import { RowActionsMenu } from './RowActionsMenu';
import { LIST_ITEM } from '../lib/motion';
import { formatSource, getInitials } from '../lib/leadMeta';
import type { Lead } from '@/types/api';

interface LeadCardProps {
  lead: Lead;
  isSelected: boolean;
  onSelect: (selected: boolean) => void;
  onUpdate: (updates: Partial<Lead>) => void;
  onDelete: () => void;
}

/** Compact mobile card — the `md:hidden` counterpart to `LeadRow`'s table row. */
export function LeadCard({ lead, isSelected, onSelect, onUpdate, onDelete }: LeadCardProps) {
  return (
    <motion.div variants={LIST_ITEM} exit="exit">
      <Card className={cn('max-w-none p-3', isSelected && 'bg-primary/5 ring-1 ring-primary/40')}>
        <div className="flex items-start gap-2.5">
          <Checkbox
            checked={isSelected}
            onChange={(e) => onSelect(e.target.checked)}
            className="mt-1.5"
            aria-label={`Select ${lead.customerName}`}
          />
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
            {getInitials(lead.customerName)}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <p className="truncate text-sm font-medium text-foreground">{lead.customerName}</p>
              <span className="shrink-0 text-[11px] text-muted-foreground">
                {formatDistanceToNow(new Date(lead.createdAt), { addSuffix: true })}
              </span>
            </div>
            <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{lead.message}</p>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <StatusBadge status={lead.status} />
              <PriorityBadge priority={lead.priority} />
              <span className="ml-auto flex items-center gap-1.5">
                <PlatformChip platform={lead.platform} />
                <span className="text-[11px] text-muted-foreground">{formatSource(lead.source)}</span>
              </span>
            </div>
          </div>
        </div>

        <div className="mt-2.5 flex items-center justify-between gap-1.5 border-t border-border/70 pt-2.5">
          <button
            type="button"
            className="text-xs font-medium text-primary hover:underline"
            onClick={() => onUpdate({ status: 'contacted' })}
          >
            Mark contacted
          </button>
          <RowActionsMenu
            label={lead.customerName}
            onMarkContacted={() => onUpdate({ status: 'contacted' })}
            onMarkQualified={() => onUpdate({ status: 'qualified' })}
            onSetHighPriority={() => onUpdate({ priority: 'high' })}
            onDelete={onDelete}
          />
        </div>
      </Card>
    </motion.div>
  );
}
